import React, { useEffect, useState } from "react";
import background from "../assets/background.webp";
import bg from "../assets/1big.jpg";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

const ServicesPage = () => {
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    setMounted(false);
    const t = setTimeout(() => {
      setMounted(true);
    }, 100);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const services = [
    {
      title: t("services_page_items.0.title"),
      desc: t("services_page_items.0.desc"),
      icon: (
        <svg
          className="w-10 h-10 text-yellow-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
        </svg>
      ),
    },
    {
      title: t("services_page_items.1.title"),
      desc: t("services_page_items.1.desc"),
      icon: (
        <svg
          className="w-10 h-10 text-yellow-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M12 2v20M2 12h20" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      ),
    },
    {
      title: t("services_page_items.2.title"),
      desc: t("services_page_items.2.desc"),
      icon: (
        <svg
          className="w-10 h-10 text-yellow-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M3 10h18M6 10v8a2 2 0 002 2h8a2 2 0 002-2v-8" />
          <path d="M8 4l4-4 4 4" />
          <path d="M12 2v8" />
        </svg>
      ),
    },
    {
      title: t("services_page_items.3.title"),
      desc: t("services_page_items.3.desc"),
      icon: (
        <svg
          className="w-10 h-10 text-yellow-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <rect x="1" y="6" width="16" height="10" rx="1.5" />
          <path d="M17 6h3l2 3v7h-5" />
          <circle cx="6" cy="16" r="2.5" />
          <circle cx="18" cy="16" r="2.5" />
          <rect
            x="4"
            y="8"
            width="8"
            height="3"
            rx="0.5"
            fill="currentColor"
            fillOpacity="0.3"
          />
          <line
            x1="8"
            y1="8"
            x2="8"
            y2="6"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <line
            x1="6"
            y1="9"
            x2="4"
            y2="7"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <line
            x1="10"
            y1="9"
            x2="12"
            y2="7"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      ),
    },
  ];

  return (
    <section
      style={{ backgroundImage: `url(${background})` }}
      className="w-full pt-24 bg-slate-800 bg-center text-white bg-fixed overflow-hidden"
    >
      <Helmet>
        <title>Solar Holding Services - Comprehensive Solar Solutions</title>
        <meta
          name="description"
          content="Explore our comprehensive solar energy services including installation, maintenance, and consulting for residential and commercial projects."
        />
        <meta
          name="keywords"
          content="solar services, solar installation, solar maintenance, renewable energy services, Solar Holding"
        />
        <meta
          property="og:title"
          content="Solar Holding Services - Comprehensive Solar Solutions"
        />
        <meta
          property="og:description"
          content="Professional solar energy services for sustainable power solutions."
        />
        <link rel="canonical" href="https://yourdomain.com/services" />
      </Helmet>
      {/* HERO */}
      <div
        className={`text-center py-24 px-4 transition-all duration-700 ease-out
        ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <h1 className="text-[20px] md:text-[64px] font-['Cormorant_Garamond'] uppercase tracking-[0.15em]">
          {t("services_page_title")}
        </h1>

        <div className="w-24 h-1 bg-yellow-500 mx-auto mt-6 mb-4" />

        <p className="text-yellow-400 tracking-[0.4em] uppercase text-sm">
          {t("services_page_subtitle")}
        </p>

        <p className="max-w-2xl mx-auto mt-6 text-gray-300 leading-7">
          {t("services_page_intro")}
        </p>
      </div>

      {/* SERVICES - с анимацией по очереди */}
      <div className="flex flex-wrap justify-center gap-10 px-6 pb-24">
        {services.map((item, i) => (
          <div
            key={i}
            className={`
              w-[380px] p-8 bg-[#415069] rounded-2xl
              transition-all duration-500 ease-out
              hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] 
              ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}
            `}
            style={{ transitionDelay: mounted ? `${i * 100}ms` : "0ms" }}
          >
            <div className="mb-4 transform transition-transform duration-300 group-hover:scale-110">
              {item.icon}
            </div>

            <h3 className="text-xl uppercase tracking-[0.1em] mb-3 font-semibold">
              {item.title}
            </h3>

            <p className="text-white text-sm leading-6">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* ADVANTAGES */}
      <div
        style={{ backgroundImage: `url(${background})` }}
        className={`bg-[#0058a3] py-20 text-center px-4 transition-all duration-700 ease-out
        ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <h2 className="text-3xl uppercase tracking-[0.2em] mb-10">
          {t("services_advantages_title")}
        </h2>

        <div className="flex flex-wrap justify-center gap-10">
          {t("services_advantages_list", { returnObjects: true }).map(
            (item, i) => (
              <div
                key={i}
                className={`
                text-yellow-400 tracking-[0.3em] text-lg font-medium
                transition-all duration-500 ease-out
                ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
              `}
                style={{ transitionDelay: mounted ? `${i * 100}ms` : "0ms" }}
              >
                {item}
              </div>
            ),
          )}
        </div>
      </div>

      {/* CTA */}
      <div
        className={`
          relative text-center py-20 bg-cover bg-center
          transition-all duration-700 ease-out
          ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
        `}
        style={{ backgroundImage: `url(${bg})` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10">
          <h2 className="text-2xl md:text-3xl mb-6 uppercase tracking-[0.2em] text-white">
            {t("services_cta_title")}
          </h2>

          <a
            href="tel:077222673"
            className="inline-block px-8 py-3 bg-yellow-400 !text-black uppercase tracking-[0.2em] 
             hover:bg-yellow-300 transition-all duration-300 
             hover:scale-105 active:scale-95 no-underline"
          >
            {t("services_cta_button")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesPage;
